import { useTranslation } from 'react-i18next';
import { Store } from 'lucide-react';
import { Button } from '~/components/ui';
import {
  ContactCard,
  type ContactDetails,
} from '~/components/cards/ContactCard';

export function SellingInformationCard({
  sellingOrganizationName,
  sellingOrganizationFacility,
  contacts = [],
  primaryContactId,
  onResendEmail,
  isResendingEmail,
  resendEmailDisabledReason,
}: Readonly<{
  sellingOrganizationName: string;
  sellingOrganizationFacility: string;
  contacts?: ContactDetails[];
  primaryContactId?: string;
  onResendEmail?: () => void;
  isResendingEmail?: boolean;
  resendEmailDisabledReason?: string;
}>) {
  const { t } = useTranslation('order');
  const isPrimary = (contact: ContactDetails) =>
    contact.id === primaryContactId;
  const orderedContacts = [
    ...contacts.filter(isPrimary),
    ...contacts.filter((contact) => !isPrimary(contact)),
  ];

  return (
    <div
      data-testid="selling-information-card"
      className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <Store size={20} className="text-prim-500" />
        <h2 className="text-neutral-900 font-semibold">
          {t('order.seller_information')}
        </h2>
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Supplier</p>
          <p className="text-sm text-neutral-900 font-medium">
            {sellingOrganizationName}
          </p>
        </div>
        {orderedContacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            compact
            primary={isPrimary(contact)}
          />
        ))}
        <div>
          <p className="text-xs text-gray-500 mb-1">{t('order.facility')}</p>
          <p className="text-sm text-neutral-900 font-medium">
            {sellingOrganizationFacility}
          </p>
        </div>
        {onResendEmail && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onResendEmail}
            loading={isResendingEmail}
            disabled={resendEmailDisabledReason !== undefined}
            title={resendEmailDisabledReason}
          >
            {t('order.resend_order_placed_email')}
          </Button>
        )}
        {onResendEmail && resendEmailDisabledReason !== undefined && (
          <p className="text-xs text-neut-500">{resendEmailDisabledReason}</p>
        )}
      </div>
    </div>
  );
}
